<template>
  <section-component
    :prefix="prefix"
    :next="next"
    :prev="prev"
    :reset="reset"
    :states="states"
    ref="section"
    @go-home="goHome"
    @navigate-to="navigateTo"
    @switch-annotation="switchAnnotation"
  >
    <template #intro>
      <div
        class="bread position-absolute"
        v-if="!$refs.section || $refs.section.canSwitch"
      >
        <span @click="goTop">HOME</span>
        /
        <span @click="goExplore">MODEL PREDICTIVE CONTROL</span>
        /
        <strong v-if="!$refs.section || $refs.section.currentAnnotation === null">BENEFITS BY AREA</strong>
        <template v-if="$refs.section && $refs.section.currentState">
          <span @click="goHome">BENEFITS BY AREA</span>
          /
          <strong>{{ $refs.section.currentState.title.toUpperCase() }}</strong>
        </template>
      </div>
      <div class="blue-gradient">
      </div>
      <div class="default position-absolute">
        <b-row>
          <b-col md="5">
            <h1>Model Predictive Control</h1>
          </b-col>
        </b-row>
        <b-row>
          <b-col md="6">
            <div class="buttons">
              <b-button pill :pressed="true" variant="outline-primary" :to="{ name: 'Benefits' }">
                BENEFITS BY AREA
              </b-button>
              <b-button pill variant="outline-primary" :to="{ name: 'MPC' }">MPC TECHNOLOGY</b-button>
            </div>
            <p>Click the cement equipment to learn about the benefits of Model Predictive
            Control.</p>
          </b-col>
        </b-row>
      </div>
      <div class="bottom position-absolute">
        <b-row>
          <b-col class="characteristics" cols="12" md="5">
            <h3>Process Characteristics</h3>
            <ul>
              <li>Unreliable, infrequent or lack of critical measurements</li>
              <li>Inertia of the process often obscures understanding of the impacts of changes made by operators</li>
              <li>Multivariable characteristics of the process</li>
              <li>High degree of disturbances</li>
            </ul>
          </b-col>
          <b-col class="divider d-xs-none d-md-block" md="1">
            <img src="@/assets/img/benefits/vertical-yellow-line.svg" />
          </b-col>
          <b-col class="mpc" cols="12" md="5">
            <h3>MPC helps to achieve the process objectives in such complex operations</h3>
            <ul>
              <li>Increased throughput</li>
              <li>Lower fuel consumption</li>
              <li>Lower energy consumption</li>
              <li>Lower emissions</li>
              <li>Better product quality</li>
              <li>Better process stability</li>
            </ul>
          </b-col>
        </b-row>
      </div>
    </template>
    <template #annotations>
      <annotation id="BlenderAnnotation0" :current-annotation="currentAnnotation">
        <h2>BLENDING</h2>
        <return-button @go-home="goHome" />
        <p>Unstable chemistry in raw materials may lead to unstable kiln operation.</p>
        <b-row>
          <b-col>
            <h3>Challenges</h3>
            <ul>
              <li>Variability of raw materials</li>
              <li>Chemistry variations in the overall material blending process</li>
              <li>Blending process controlled manually in several operations</li>
              <li>Major impact of kiln stability</li>
            </ul>
          </b-col>
          <b-col>
            <h3>MPC Benefits</h3>
            <ul>
              <li>Reduce blend variability</li>
              <li>Reduce blend costs</li>
              <li>Improve raw material resource usage</li>
              <li>Improve kiln performance</li>
              <li>Raw material cost reduction</li>
            </ul>
          </b-col>
        </b-row>
        <img src="@/assets/img/benefits/potential-gains-graphic.svg" />
        <div class="text-center">
          <h3>Potential Gains</h3>
          <p>Lime saturation factor (LSF) typical deviation reduction up to 40%</p>
        </div>
      </annotation>
      <annotation id="GrindingAnnotation0" :current-annotation="currentAnnotation">
        <h2>GRINDING</h2>
        <return-button @go-home="goHome" />
        <p>Nearly 70% of electrical energy is used for size reduction, which includes crushing and grinding of cement raw materials and clinker grinding.</p>
        <b-row>
          <b-col>
            <h3>Challenges</h3>
            <ul>
              <li>Maximize the productivity while keeping the fineness variability within the products specifications</li>
              <li>Energy consumption</li>
              <li>Lack of reliable measurements of some key variables</li>
            </ul>
          </b-col>
          <b-col>
            <h3>MPC Benefits</h3>
            <ul>
              <li>Reduce energy cost</li>
              <li>Increase throughput</li>
              <li>Reduce fineness variability</li>
              <li>Reduced maintenance costs</li>
            </ul>
          </b-col>
        </b-row>
        <img src="@/assets/img/benefits/potential-gains-graphic.svg" />
        <div>
          <h3 class="text-center">Potential Gains</h3>
          <ul class="middle">
            <li>Increases throughput: 2-10%</li>
            <li>Reduced specific power consumption: 2-10%</li>
            <li>Reduced transition time for type changes up to 40%</li>
            <li>Reduce off-spec product: 20-30%</li>
            <li>Reduced standard deviation for product residue/blaine: 20-30%</li>
          </ul>
        </div>
      </annotation>
      <annotation id="KilnAnnotation0" :current-annotation="currentAnnotation">
        <h2>CEMENT KILN</h2>
        <return-button @go-home="goHome" />
        <p>The reactions involved in cement kilns are still not well understood because of their
complexity.</p>
        <b-row>
          <b-col>
            <h3>Challenges</h3>
            <ul>
              <li>Fuel sources variability</li>
              <li>Stable operation</li>
              <li>Stabilize a temperature profile along the rotary kiln</li>
              <li>Uncertainty of raw material characteristics</li>
              <li>Complex dynamics</li>
            </ul>
          </b-col>
          <b-col>
            <h3>MPC Benefits</h3>
            <ul>
              <li>Increase throughput</li>
              <li>Decrease product variability</li>
              <li>Reduce energy costs</li>
              <li>Improve product grade</li>
              <li>Reduce emissions</li>
              <li>Improve heat recovery</li>
              <li>Longer campaign run and refractory life</li>
            </ul>
          </b-col>
        </b-row>
        <img src="@/assets/img/benefits/potential-gains-graphic.svg" />
        <div>
          <h3 class="text-center">Potential Gains</h3>
          <ul class="middle">
            <li>Increases throughput: 2% - 5%</li>
            <li>Fuel consumption reduction: 2% – 5%</li>
            <li>Power consumption reduction: 1% - 3%</li>
            <li>Free lime deviation reduction: up to 50%</li>
            <li>Reducing NOx emissions up to 20%</li>
          </ul>
        </div>
      </annotation>
    </template>
  </section-component>
</template>

<script>
import util from '@/assets/js/util'

import Annotation from '@/components/Annotation'
import ReturnButton from '@/components/ReturnButton'
import SectionComponent from '@/components/Section'

export default {
  components: { Annotation, ReturnButton, SectionComponent },
  data () {
    return {
      currentAnnotation: null,
      prefix: 'benefits',
      states: [
        {
          id: 'Blender',
          showContentAt: [0.0],
          title: 'Blending',
          videos: [false]
        },
        {
          id: 'Grinding',
          showContentAt: [0.0],
          title: 'Grinding',
          videos: [false]
        },
        {
          id: 'Kiln',
          showContentAt: [0.0],
          title: 'Cement Kiln',
          videos: [false]
        }
      ]
    }
  },
  methods: {
    findVideo (mp4) {
      return util.staticPrefix() + 'mp4/' + this.prefix + '/' + mp4
    },
    goExplore () {
      this.$router.push({ name: 'Explore' })
    },
    goHome () {
      this.$router.push({ name: 'Benefits' })
    },
    goToEquipment () {
      if (this.equipment) {
        let index = -1
        this.states.forEach((state, pos) => {
          if (state.id.toLowerCase() === this.equipment) {
            index = pos
          }
        })
        if (index > -1) {
          this.$refs.section.switchAnnotation(index)
        }
      } else {
        this.$refs.section.resetScene()
      }
    },
    goTop () {
      this.$router.push({ name: 'Home' })
    },
    navigateTo (index) {
      console.log('benefits.navigateTo: ' + index)
      this.$router.push({
        name: 'BenefitsAnnotation',
        params: { equipment: this.states[index].id.toLowerCase() }
      })
    },
    switchAnnotation (annotation) {
      this.currentAnnotation = annotation
    }
  },
  mounted () {
    this.$store.commit('showLogo', true)
    if (this.equipment) {
      this.goToEquipment()
    }
  },
  name: 'Benefits',
  props: {
    equipment: {
      type: String,
      default: null
    },
    next: {
      type: Number,
      default: 0
    },
    prev: {
      type: Number,
      default: 0
    },
    reset: {
      type: Boolean,
      default: false
    }
  },
  watch: {
    equipment: function (val) {
      this.goToEquipment()
    },
    reset: function (val) {
      if (val) {
        this.resetScene()
      }
    }
  }
}
</script>

<style lang="scss">
.default, #annotationContainer {
  height: 93%;
  top: 7%;
}

.section {
  .bread {
    left: 3%;
    top: 3%;
    z-index: 100;
  }
}

#app .zoomed {
  .default, .bottom {
    display: none;
  }

  #annotationContainer {
    background-size: cover;
    overflow-x: hidden;
    overflow-y: auto;
    position: fixed;
  }
}

#app {
  #annotationContainer {
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    padding-left: 3%;
    padding-top: 3%;
    position: absolute;

    h2 {
      color: var(--yellow);
    }

    .middle {
      padding-left: 32%;
      width: 100%;
    }

    .btn {
      margin-bottom: 20px!important;
      margin-top: 15px!important;
    }
  }
}

#benefits {
  background-image: url('../assets/img/benefits/still.png');
  background-size: contain;

  &.zoomed {
    background-image: none;
    height: 100%;
  }
}

#btnContainer .btn {
  background: rgba(0, 0, 0, 0.6);
}

#BlenderBtn {
  left: 17%;
  top: 50%;
}

#KilnBtn {
  left: 45%;
  top: 50%;
}

#GrindingBtn {
  left: 75%;
  top: 55%;
}
</style>
